/**
@prop --ux-input-height: the height of the field itself (without labels)
@prop --ux-input-font-size: font-size of the input
@prop --ux-input-font-weight: font-weight of the input
@prop --ux-input-line-height: line-height of the input
@prop --ux-input-padding-horizontal: horizontal inner padding
@prop --ux-input-padding-vertical: vertical inner padding
@prop --ux-input-padding: combined vertical and horizontal padding
@prop --ux-input-spacing: spacing between field elements
@prop --ux-input-outline-width: outline thickness used when the field has focus
@prop --ux-input-border-width: border thickness of the input
@prop --ux-input-border-color: border-color of the input
@prop --ux-input-error-color: text color of the input in error state
@prop --ux-input-error-border-color: border-color of the input in error state
@prop --ux-input-placeholder-color: color of the placeholder text
@prop --ux-input-placeholder-font-weight: font-weight of the placeholder text
@prop --ux-input-focus-border-color: border-color of the input when focussed
@prop --ux-input-focus-text-color: text color of the input when focussed
@prop --ux-input-hover-bg: background-color of the input when hovered
@prop --ux-input-hover-border-color: border-color of the input when hovered
@prop --ux-input-hover-text-color: text color of the input when hovered
@prop --ux-input-active-bg: background-color of the input when active
@prop --ux-input-active-border-color: border-color of the input when active
@prop --ux-input-active-text-color: text color of the input when active
@prop --ux-input-disabled-bg: background-color when disabled
@prop --ux-input-disabled-border-color: border-color when disabled
@prop --ux-input-disabled-text-color: text color when disabled
@prop --ux-input-disabled-opacity: opacity of the input when disabled
@prop --ux-input-hint-margin: outer margin of the hint block below the input
@prop --ux-input-hint-color: color of the hint text
@prop --ux-input-hint-font-size: font-size of the hint text
@prop --ux-input-hint-font-weight: font-weight of the hint text
@prop --ux-input-hint-line-height: line-height of the hint text
@prop --ux-input-icon-size: size of the icon displayed inside the field
 */

:host(ux-input) {
  box-sizing: border-box;
  display: block;
  margin: 0;
  max-width: 100%;
  position: relative;
}

:host([disabled]) {
  pointer-events: none;
}

:host([type="hidden"]) {
  display: none;
}

input,
::slotted(input) {
  box-sizing: border-box !important;
  display: inline-block;
  width: 100%;
  background-color: transparent;
  border: 0 none transparent;
  color: inherit;
  flex: 1 1 auto;
  font-family: var(--ux-font) !important;
  font-size: var(--ux-input-font-size) !important;
  font-weight: var(--ux-input-font-weight);
  height: 100%;
  line-height: var(--ux-input-line-height);
  padding: var(--ux-input-padding);

  :host([disabled]) & {
    background: var(--ux-input-disabled-bg);
    border-color: var(--ux-input-disabled-border-color);
    color: var(--ux-input-disabled-text-color);
  }
}

input:focus,
input:focus-visible,
::slotted(input:focus),
::slotted(input:focus-visible) {
  outline: 0;
}

input::placeholder,
::slotted(input)::placeholder {
  color: var(--ux-input-placeholder-color);
  font-weight: var(--ux-input-placeholder-font-weight);
}

.controls {
  background: var(--ux-input-background-color);
  border: var(--ux-input-border-width) solid var(--ux-input-border-color);
  display: flex;
  flex: 1 1 auto;
  flex-direction: row;
  flex-wrap: nowrap;
  height: var(--ux-input-height);
  position: relative;

  &:hover {
    background: var(--ux-input-hover-bg);
    border-color: var(--ux-input-hover-border-color);
    color: var(--ux-input-hover-text-color);
  }

  &:active {
    background: var(--ux-input-active-bg);
    border-color: var(--ux-input-active-border-color);
    color: var(--ux-input-active-text-color);
  }

  :host([invalid]) & {
    border-color: var(--ux-input-error-border-color);
  }

  :host([invalid]):hover & {
    border-color: var(--ux-input-error-hover-border-color);
  }

  :host(.is-focused) & {
    color: var(--ux-input-focus-text-color);
    outline: var(--ux-input-outline-width) solid var(--ux-input-focus-border-color);
    outline-offset: var(--ux-input-outline-offset);
  }

  :host([disabled]) & {
    border-color: var(--ux-input-disabled-border-color);
    opacity: var(--ux-input-disabled-opacity);
  }
}

label {
  display: inline-block;
  font-family: var(--ux-font);
  font-size: var(--ux-label-font-size);
  font-weight: var(--ux-label-font-weight);
  margin: var(--ux-label-margin);
  padding: var(--ux-label-padding);
  text-transform: var(--ux-label-text-transform);
  vertical-align: middle;
}

.invalid-message {
  color: $color-red-error;
}
